import React, { useState, useEffect } from 'react';
import { CheckCircle, Award, Users, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [header, setHeader] = useState({
    label: "About Us",
    title: "Your Trusted Partner in Business Transformation",
    description: "Shire Advisors, LLC was formed to provide strategic consulting services, third party financial analysis and non-conventional financing to stagnant, underperforming or distressed small businesses and organizations."
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamRes, headerRes] = await Promise.all([
          axios.get(`${API}/content/team`),
          axios.get(`${API}/content/section-headers/about`)
        ]);
        // Filter to only show visible members and sort by order
        const visibleMembers = teamRes.data
          .filter(member => member.visible !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setTeamMembers(visibleMembers);
        if (headerRes.data) {
          setHeader(headerRes.data);
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
      }
    };

    fetchData();
  }, []);

  const highlights = [
    "Independent third-party analysis with no conflicts of interest",
    "Access to non-conventional financing solutions",
    "Hands-on strategic guidance and implementation",
    "Complete confidentiality and discretion guaranteed",
  ];

  return (
    <section id="about" className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* About Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Left - Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=800&h=600&fit=crop"
                alt="Shire Advisors team collaborating on client strategy"
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" />
            </div>
            {/* Floating Stats Card */}
            <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-6 hidden md:block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-gold-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-navy-900">25+</div>
                  <div className="text-sm text-gray-500">Years of Excellence</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block text-gold-600 font-semibold text-sm tracking-wider uppercase mb-4">
                {header.label}
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-navy-900 mb-6">
                {header.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {header.description}
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our team combines decades of experience in corporate finance, technology, and business strategy to deliver results that transform struggling businesses into thriving enterprises.
              </p>
            </div>

            {/* Highlights */}
            <div className="space-y-4">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div id="team" className="scroll-mt-[140px]">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <span className="inline-block text-gold-600 font-semibold text-sm tracking-wider uppercase mb-4">
              Our Leadership
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-navy-900 mb-6">
              Meet the Team
            </h2>
            <p className="text-lg text-gray-600">
              Experienced professionals dedicated to your success
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="group text-center w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] max-w-[280px]"
              >
                <div className="relative mb-6 overflow-hidden rounded-xl">
                  <img
                    src={member.image}
                    alt={`${member.name}${member.role ? ' — ' + member.role : ''}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-square object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-1">{member.name}</h3>
                <p className="text-gold-600 font-medium text-sm mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
